import React from 'react';
import dayjs from 'dayjs';
import { Box, Chip, Typography } from '@mui/material';
import { BiTime } from 'react-icons/bi';
import type { TpQuote } from '../thirdParty/types';

interface ManagedQuoteCardProps {
  quote: TpQuote;
}

const centsToDollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * THE price for a HopDrive Managed delivery. The dealer doesn't pick a vendor —
 * the selection algorithm does (mock: cheapest; real: server-side selection
 * strategy) — so this shows one brand-neutral price with ETA and a live expiry
 * countdown. The winning vendor still rides the quote internally for booking.
 */
const ManagedQuoteCard: React.FC<ManagedQuoteCardProps> = ({ quote }) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!quote.expires_at) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [quote.expires_at]);

  const expiresMs = quote.expires_at ? new Date(quote.expires_at).getTime() - now : null;
  const expired = expiresMs !== null && expiresMs <= 0;
  const countdown =
    expiresMs === null || expired
      ? null
      : `${Math.floor(expiresMs / 60000)}:${String(Math.floor((expiresMs % 60000) / 1000)).padStart(2, '0')}`;
  const etaLabel = quote.dropoff_eta ? `~${Math.max(1, dayjs(quote.dropoff_eta).diff(dayjs(), 'minute'))} min` : null;

  return (
    <Box
      data-testid='pd-managed-quote'
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1.5,
        border: 2, borderColor: expired ? 'divider' : 'primary.main', borderRadius: 1.5,
        bgcolor: expired ? 'transparent' : 'action.hover',
        opacity: expired ? 0.6 : 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
            ⚡ HopDrive Managed
          </Typography>
          {etaLabel && !expired ? (
            <Chip icon={<BiTime size={12} />} label={etaLabel} size='small' sx={{ height: 18, fontSize: 10 }} />
          ) : null}
        </Box>
        {expired ? (
          <Typography variant='body2' sx={{ fontWeight: 600, mt: 0.75 }} color='error.main'>
            Quote expired — get a fresh quote
          </Typography>
        ) : countdown ? (
          <Typography variant='body2' color='text.secondary' sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.75 }}>
            Quote expires in
            <Typography
              component='span'
              sx={{ fontWeight: 700, fontSize: 17, color: 'text.primary', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
            >
              {countdown}
            </Typography>
          </Typography>
        ) : (
          <Typography variant='caption' color='text.secondary'>
            delivery fee
          </Typography>
        )}
      </Box>
      <Typography variant='h5' sx={{ fontWeight: 800 }}>
        {centsToDollars(quote.fee_cents)}
      </Typography>
    </Box>
  );
};

export const isQuoteExpired = (quote: TpQuote): boolean =>
  !!quote.expires_at && new Date(quote.expires_at).getTime() <= Date.now();

export default ManagedQuoteCard;
